import React from 'react';

import { LayoutComposer, NamedLink } from '../../components';
import FooterContainer from '../FooterContainer/FooterContainer';
import StaticPage from '../PageBuilder/StaticPage';
import { SearchCTA } from '../PageBuilder/Primitives/SearchCTA';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import css from './CustomLandingPage.module.css';

const HOME_IMAGES = {
  family: '/static/images/homepage/family-search-support.png',
  choose: '/static/images/homepage/choose-an-angel.png',
  book: '/static/images/homepage/book-support.png',
  angel: '/static/images/homepage/become-an-angel.png',
};

// Preserve the operator-managed "Tell us what you need" visual as the platform benefits image.
const getAssetImageUrl = media => {
  const variants = Object.values(media?.image?.attributes?.variants || {});
  const largestVariant = variants.sort((a, b) => (b.width || 0) - (a.width || 0))[0];
  return largestVariant?.url;
};

const CheckIcon = () => (
  <svg aria-hidden="true" className={css.checkIcon} viewBox="0 0 24 24">
    <path d="m5 12 4.2 4.2L19.5 6" fill="none" stroke="currentColor" strokeWidth="2.6" />
  </svg>
);

const ArrowIcon = () => (
  <svg aria-hidden="true" className={css.arrowIcon} viewBox="0 0 24 24">
    <path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const ShieldIcon = () => (
  <svg aria-hidden="true" className={css.trustIcon} viewBox="0 0 24 24">
    <path
      d="M12 3 19 6v5c0 4.7-3 8-7 10-4-2-7-5.3-7-10V6l7-3Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path d="m8.5 12 2.2 2.2 4.8-5" fill="none" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const ClockIcon = () => (
  <svg aria-hidden="true" className={css.trustIcon} viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7v5l3.5 2" fill="none" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const HeartIcon = () => (
  <svg aria-hidden="true" className={css.trustIcon} viewBox="0 0 24 24">
    <path
      d="M20 8.5c0 5-8 10-8 10s-8-5-8-10a4.2 4.2 0 0 1 7.5-2.6L12 6.5l.5-.6A4.2 4.2 0 0 1 20 8.5Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

const HeroSearch = () => (
  <section className={css.hero}>
    <div className={css.heroGlowOne} />
    <div className={css.heroGlowTwo} />
    <div className={css.heroInner}>
      <p className={css.eyebrow}>A little help, right when it matters</p>
      <h1 className={css.heroTitle}>A Little Help Can Make a Big Difference.</h1>
      <p className={css.heroCopy}>
        Find background-checked companions for flexible, non-medical support at home. From rides and
        errands to meals, technology, and companionship, help is close by.
      </p>
      <div className={css.heroSearch}>
        <SearchCTA searchFields={{ categories: true, locationSearch: true, dateRange: true }} />
      </div>
      <div className={css.heroNotes}>
        <span>No subscription</span>
        <span>Transparent hourly rates</span>
        <span>Book when you need help</span>
      </div>
    </div>
  </section>
);

const WhyChooseUs = ({ platformBenefitsImage }) => (
  <section className={css.whySection}>
    <div className={css.contentFrame}>
      <div className={css.whyImageWrap}>
        <img
          className={css.whyImage}
          src={platformBenefitsImage || HOME_IMAGES.family}
          alt="MyTodayAngel platform benefits and companion support"
        />
      </div>
      <div className={css.whyCopy}>
        <p className={css.eyebrow}>For older adults and their loved ones</p>
        <h2>Support for living well at home.</h2>
        <p>
          Practical, dependable help can make living at home feel more manageable for older adults
          and bring reassurance to family caregivers and loved ones.
        </p>
        <ul className={css.checklist}>
          <li>
            <CheckIcon />
            Choose an Angel who feels like the right fit.
          </li>
          <li>
            <CheckIcon />
            See services and hourly rates before you request to book.
          </li>
          <li>
            <CheckIcon />
            Get flexible, non-medical support without a long-term commitment.
          </li>
        </ul>
        <NamedLink className={css.textLink} name="SearchPage">
          Find an Angel <ArrowIcon />
        </NamedLink>
      </div>
    </div>
  </section>
);

const steps = [
  {
    number: '01',
    title: 'Tell us what you need',
    text:
      'Search by location, service, and availability to start with the kind of support that matters today.',
    image: HOME_IMAGES.family,
    alt: 'An older adult and family member reviewing support options together',
  },
  {
    number: '02',
    title: 'Choose your Angel',
    text:
      'Compare profiles, services, rates, and experience to find someone who feels like the right fit.',
    image: HOME_IMAGES.choose,
    alt: 'A companion and older adult walking together in a neighborhood park',
  },
  {
    number: '03',
    title: 'Book the help you need',
    text: 'Connect directly and arrange support without a subscription or long-term commitment.',
    image: HOME_IMAGES.book,
    alt: 'An older adult and companion reviewing a booking confirmation on a phone',
  },
];

const HowItWorks = () => (
  <section className={css.journeySection}>
    <div className={css.sectionHeading}>
      <p className={css.eyebrow}>Simple by design</p>
      <h2>Help at home in three thoughtful steps.</h2>
    </div>
    <div className={css.journeyList}>
      {steps.map((step, index) => (
        <article className={css.journeyCard} key={step.number}>
          <div className={css.journeyImageWrap}>
            <img className={css.journeyImage} src={step.image} alt={step.alt} />
          </div>
          <div className={css.journeyContent}>
            <span className={css.stepNumber}>{step.number}</span>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
          </div>
        </article>
      ))}
    </div>
  </section>
);

const TwoAudiences = () => (
  <section className={css.audienceSection}>
    <article className={css.audienceCard}>
      <img src={HOME_IMAGES.family} alt="A family reviewing ways to arrange support" />
      <div className={css.audienceOverlay}>
        <p className={css.eyebrow}>For families</p>
        <h2>Support that fits real life.</h2>
        <NamedLink className={css.lightLink} name="SearchPage">
          Browse Angels <ArrowIcon />
        </NamedLink>
      </div>
    </article>
    <article className={css.audienceCard}>
      <img src={HOME_IMAGES.angel} alt="A prospective companion greeting an older adult at home" />
      <div className={css.audienceOverlay}>
        <p className={css.eyebrow}>For companions</p>
        <h2>Make a meaningful difference on your terms.</h2>
        <p className={css.audienceCopy}>
          Choose the services you offer, set your hourly rate, and share when you are available.
        </p>
        <NamedLink className={css.lightLink} name="CompanionDashboardPage">
          Create Your Angel Profile <ArrowIcon />
        </NamedLink>
      </div>
    </article>
  </section>
);

const TrustStrip = () => (
  <section className={css.trustSection}>
    <div className={css.sectionHeading}>
      <p className={css.eyebrow}>A calmer way to find help</p>
      <h2>Made for confidence, not complexity.</h2>
    </div>
    <div className={css.trustGrid}>
      <article>
        <ShieldIcon />
        <h3>Background checked</h3>
        <p>More peace of mind before you connect.</p>
      </article>
      <article>
        <HeartIcon />
        <h3>Choose your Angel</h3>
        <p>Find a person and service that feel right.</p>
      </article>
      <article>
        <ClockIcon />
        <h3>Flexible by nature</h3>
        <p>Book the support you need, when you need it.</p>
      </article>
    </div>
  </section>
);

const CustomLandingPage = props => {
  const { pageAssetsData } = props;
  const meta = pageAssetsData?.meta || {};
  const platformBenefitsImage = getAssetImageUrl(pageAssetsData?.sections?.[1]?.blocks?.[0]?.media);
  const title = meta?.pageTitle?.content;
  const description = meta?.pageDescription?.content;
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <StaticPage title={title} description={description}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {({ Topbar, Main, Footer }) => (
          <>
            <Topbar as="header" className={css.topbar}>
              <TopbarContainer currentPage="LandingPage" />
            </Topbar>
            <Main as="main" id="main-content" className={css.main}>
              <HeroSearch />
              <WhyChooseUs platformBenefitsImage={platformBenefitsImage} />
              <HowItWorks />
              <TwoAudiences />
              <TrustStrip />
            </Main>
            <Footer>
              <FooterContainer />
            </Footer>
          </>
        )}
      </LayoutComposer>
    </StaticPage>
  );
};

export default CustomLandingPage;
